import { ReactNode } from 'react';
import { Layout as AntLayout, Avatar, Typography, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import logoThiran from '../assets/thiran_white.svg';

const { Header, Content } = AntLayout;
const { Text } = Typography;

interface AppStoreLayoutProps {
  children: ReactNode;
}

const AppStoreLayout = ({ children }: AppStoreLayoutProps) => {
  const { user, logout } = useAuth();
  const { themeConfig } = useTheme();
  const navigate = useNavigate();

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/app/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/app/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: logout,
    },
  ];

  return (
    <AntLayout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Top Header */}
      <Header
        style={{
          padding: '0 24px',
          background: themeConfig.primaryColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={logoThiran}
            alt="ThiranTech"
            style={{
              height: '36px',
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Text
            style={{
              color: '#ffffff',
              fontSize: '18px',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
          >
            App Store
          </Text>
        </div>

        {/* User Profile */}
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              transition: 'background 0.2s',
            }}
          >
            <Avatar
              icon={<UserOutlined />}
              size={32}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Text
                  style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '1',
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </Text>
                <span
                  style={{
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: user?.role === 'admin' || user?.role === 'superadmin' 
                      ? '#52c41a' 
                      : '#1890ff',
                    color: '#ffffff',
                    lineHeight: '1',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {user?.role || 'user'}
                </span>
              </div>
              <Text
                style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '12px',
                  lineHeight: '1',
                }}
              >
                {user?.email}
              </Text>
            </div>
          </div>
        </Dropdown>
      </Header>

      {/* Main Content - No Sidebar */}
      <Content
        style={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Content>
    </AntLayout>
  );
};

export default AppStoreLayout;
